import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { getTime } from 'date-fns';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { getSchoolYear } from '../../../schoolyear/getSchoolYear.js';
import type { Person } from '../../../users/entities/person.js';
import { getPreselectedFilterURIComponent } from '../../../users/list/client/useUserListPreselectedFilter.js';
import type { UserListSelectionInterface } from '../../../users/list/entities/UserListSelectionInterface.js';
import { bookingActive } from '../../api/bookingActive.js';
import { type BookingHashIngnoreKey, bookingHash } from '../../api/bookingHash.js';
import type { Booking } from '../../entities/Booking.js';
import { ClassBooking } from '../../entities/ClassBooking.js';

export type GroupedBookingPerson = NarrowedEntity<
  Person,
  { _id: 1; debitorennummer: 1; profilDaten: { stammdaten: { vorname: 1; nachname: 1 } } }
>;

export type GroupedBookingBooking = NarrowedEntity<
  Booking,
  {
    _id: 1;
    userID: 1;
    groupIDs: 1;
    startDate: 1;
    groups: {
      _id: 1;
      name: 1;
      billingPosition: { name: 1; _id: 1; priceInCents: 1; rhythm: 1; kuerzel: 1 };
    };
    endDate: 1;
    billingPosition: { priceInCents: 1 };
    classIdentifier: 1;
    grade: 1;
    bookingReference: 1;
  }
>;

export interface GroupedBooking {
  users: GroupedBookingPerson[];
  bookings: GroupedBookingBooking[];
  hash: string;
  active: boolean;
  label: Renderable;
}

function buildPersonFilterForBooking(
  booking: GroupedBooking,
  ignoreGroupingKeys: BookingHashIngnoreKey[],
  personSelectionInterface: UserListSelectionInterface | null,
) {
  const firstBooking = booking.bookings[0];
  if (!firstBooking) throw new Error('First booking not found. Should be ensured by the logic above.');

  // deconstruct the list selectionset the user has opened the mass editor with. If we only filter persons by their bookings we can get more than the user initially selected so we need to narrow it down again
  const personFilter = personSelectionInterface?.filter ?? new AdornisFilter({});

  // the filter is trivial but we need to create aspectes for the explictily deselected and selected persons
  const selectedPersonIDFilter = personSelectionInterface?.userIDs?.length
    ? new ListAspect({
        fieldName: '_id',
        values: personSelectionInterface.userIDs,
      })
    : undefined;

  const deselectedPersonIDFilter = personSelectionInterface?.explicitDeselectedUserIDs?.length
    ? new ListAspect({
        fieldName: '_id',
        mode: 'containsNONE',
        values: personSelectionInterface.explicitDeselectedUserIDs,
      })
    : undefined;

  return new AdornisFilter({
    searchFields: personFilter.searchFields,
    search: personFilter.search,
    aspects: [
      ...personFilter.aspects,
      ...(selectedPersonIDFilter ? [selectedPersonIDFilter] : []),
      ...(deselectedPersonIDFilter ? [deselectedPersonIDFilter] : []),
      new StringAspect({
        fieldName: 'buchungen._class',
        mode: 'equals',
        value: firstBooking._class,
      }),
      new ListAspect({
        fieldName: 'buchungen.groupIDs',
        mode: 'containsAND',
        values: firstBooking.groupIDs,
      }),
      ...(firstBooking instanceof ClassBooking && !ignoreGroupingKeys.includes('classIdentifier')
        ? [
            new StringAspect({
              fieldName: 'buchungen.classIdentifier',
              mode: 'equals',
              value: firstBooking.classIdentifier,
            }),
          ]
        : []),

      ...(firstBooking instanceof ClassBooking && !ignoreGroupingKeys.includes('grade')
        ? [
            new StringAspect({
              fieldName: 'buchungen.grade',
              mode: 'equals',
              value: firstBooking.grade,
            }),
          ]
        : []),
    ],
  });
}

function buildLabelForBooking(
  booking: GroupedBooking,
  ignoreGroupingKeys: BookingHashIngnoreKey[],
  personSelectionInterface: UserListSelectionInterface | null,
) {
  const firstBooking = booking.bookings[0];
  if (!firstBooking) throw new Error('First booking not found. Should be ensured by the logic above.');

  const getPersonBookingLabel = (person: GroupedBookingPerson[]) => {
    const limit = 3;
    const examplePersons = person.slice(0, limit);

    const getIdentifier = (person: GroupedBookingPerson) => {
      return person.profilDaten?.stammdaten?.vorname && person.profilDaten?.stammdaten?.nachname
        ? `${person.profilDaten?.stammdaten?.vorname} ${person.profilDaten?.stammdaten?.nachname}`
        : person.debitorennummer;
    };

    const filterForBooking = buildPersonFilterForBooking(booking, ignoreGroupingKeys, personSelectionInterface);

    return html`${repeat(
      examplePersons,
      p => p._id,
      (p, i) => html`
        <a class="link hover:text-primary" href="/personen/${p._id}"> ${getIdentifier(p)} </a>
        ${i === examplePersons.length - 1 ? '' : ', '}
      `,
    )}
    ${
      person.length > limit
        ? html` und
            <a class="link hover:text-primary" href="/personen?${getPreselectedFilterURIComponent(filterForBooking)}">
              ${person.length - limit} weitere
            </a>`
        : ''
    }`;
  };

  const groupLinks = (firstBooking.groups ?? []).map(
    group => html` <a class="truncate link hover:text-primary" href="/groups/${group._id}"> ${group.name} </a> `,
  );

  const lookingAtSingleBooking = booking.bookings.length === 1;
  const bookingCountLabel = `${booking.bookings.length} ${lookingAtSingleBooking ? 'Eintrag' : 'Einträge'}`;

  if (firstBooking instanceof ClassBooking) {
    const grade = !ignoreGroupingKeys.includes('grade') && firstBooking.grade ? firstBooking.grade : '---';
    const classIdentifier =
      !ignoreGroupingKeys.includes('classIdentifier') && firstBooking.classIdentifier
        ? firstBooking.classIdentifier
        : '---';

    const schoolYear = firstBooking.startDate ? getSchoolYear({ date: firstBooking.startDate }) : '---';

    return html`
      <div class="flex min-w-0 flex-col gap-1 py-0.5">
        <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span class="font-semibold text-base-content">Klassenbuchung</span>
          <span class="badge badge-outline badge-sm">${grade} ${classIdentifier}</span>
          <span class="badge badge-ghost badge-sm">${schoolYear}</span>
          ${
            lookingAtSingleBooking
              ? html`<span class="badge badge-ghost badge-sm">Ref:${booking.bookings.at(0)?.bookingReference}</span>`
              : html`<span class="badge badge-ghost badge-sm">${bookingCountLabel}</span>`
          }
        </div>

        <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-base-content/70">
          <span class="shrink-0">Gruppe:</span>
          <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">${groupLinks}</div>
        </div>

        <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-base-content/70">
          <span class="shrink-0">Personen:</span>
          <div class="min-w-0 flex-1">${getPersonBookingLabel(booking.users)}</div>
        </div>
      </div>
    `;
  } else {
    const dateRange = `${firstBooking.startDate ? formatCalendarDay(firstBooking.startDate, 'dd.MM.yyyy') : '---'} - ${
      firstBooking.endDate ? formatCalendarDay(firstBooking.endDate, 'dd.MM.yyyy') : 'Offenes Ende'
    }`;

    return html`
      <div class="flex min-w-0 flex-col gap-1 py-0.5">
        <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span class="font-semibold text-base-content">Buchung</span>
          <span class="badge badge-outline badge-sm">${dateRange}</span>
          ${
            lookingAtSingleBooking
              ? html`<span class="badge badge-ghost badge-sm">Ref:${booking.bookings.at(0)?.bookingReference}</span>`
              : html`<span class="badge badge-ghost badge-sm">${bookingCountLabel}</span>`
          }
        </div>

        <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-base-content/70">
          <span class="shrink-0">Gruppe:</span>
          <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">${groupLinks}</div>
        </div>

        <div class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-base-content/70">
          <span class="shrink-0">Personen:</span>
          <div class="min-w-0 flex-1">${getPersonBookingLabel(booking.users)}</div>
        </div>
      </div>
    `;
  }
}

/**
 * Groups bookings by their hash
 * @param bookings
 * @param persons
 * @returns grouped bookings
 */
export const useGroupedBookings = (
  bookings: Maybe<GroupedBookingBooking[]>,
  persons: Maybe<GroupedBookingPerson[]>,
  ignoreKeys: BookingHashIngnoreKey[] = ['userID'],
  personSelectionInterface: UserListSelectionInterface | null = null,
) => {
  const [groupedBookingsMap, setGroupedBookingsMap] = useState<Map<string, GroupedBooking>>(new Map());
  const [groupedBookings, setGroupedBookings] = useState<GroupedBooking[]>([]);
  const [loading, setLoading] = useState(true);

  // Handle async hashing when bookings change
  useEffect(() => {
    if (!bookings || !persons) return;

    setLoading(true);

    const processBookings = async () => {
      const newGroupedBookings = new Map<string, GroupedBooking>();

      for (const booking of bookings) {
        const hash = await bookingHash(booking, ignoreKeys);

        // ensure the hash has an entry
        if (!newGroupedBookings.has(hash))
          newGroupedBookings.set(hash, { users: [], bookings: [], hash, active: false, label: '' });

        // get the entry
        const groupedBooking = newGroupedBookings.get(hash);

        if (!groupedBooking) throw new Error('Grouped Booking not found. Should be ensured by the logic above.');

        // add the booking
        groupedBooking.bookings.push(booking);

        // add the user
        const user = persons.find(p => p._id === booking.userID);
        if (!user)
          throw new Error(
            `User with id ${booking.userID} not found in array. Make sure that the person looking here can see all the users from the bookings. Should never happen as the bookings are filtered by the persons.`,
          );
        groupedBooking.users.push(user);
      }

      // set things which can be inferred from just one booking
      // we do this outside of the loop above to save on processing
      for (const groupedBooking of newGroupedBookings.values()) {
        // we can use the first booking as it will always be there and also be the same as all other bookings in the group
        const firstBooking = groupedBooking.bookings[0];
        if (!firstBooking) throw new Error('First booking not found. Should be ensured by the logic above.');

        // set active
        groupedBooking.active = bookingActive(firstBooking);

        // set label
        groupedBooking.label = buildLabelForBooking(groupedBooking, ignoreKeys, personSelectionInterface);
      }

      const groupedArray = Array.from(newGroupedBookings.values());
      groupedArray.sort((a, b) => {
        if (a.active && !b.active) return -1;
        if (!a.active && b.active) return 1;
        // Sort by startDate (most recent first)
        return a.bookings[0]?.startDate && b.bookings[0]?.startDate
          ? getTime(b.bookings[0].startDate) - getTime(a.bookings[0].startDate)
          : 0;
      });

      setGroupedBookings(groupedArray);

      setGroupedBookingsMap(newGroupedBookings);
      setLoading(false);
    };

    void processBookings();
  }, [bookings, ignoreKeys]);

  return {
    groupedBookingsMap,
    groupedBookings,
    loading,
  };
};
